import { BrainCircuitIcon, FilterIcon } from "lucide-react";
import { useCallback } from "react";
import {
  T,
  RecordProps,
  Editor,
  TLShapeUtilCanBindOpts,
  useEditor,
  useValue,
} from "tldraw";

import {
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
  BlockBody,
} from "@/components/canvas-editor/components/Container";
import { CanvasSimpleSelect } from "@/components/canvas-editor/components/Inputs";
import {
  NodeDocsDescription,
  NodeDocsHeader,
  useExampleShape,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
  dropTargetId,
} from "@/components/canvas-editor/shapes/bindable";
import {
  Dimensions,
  LogicalOp,
  logicalOpOptions,
  logicalOps,
  LogicProps,
  LogicShape,
  logicType,
  pinnableShapes,
  metadataType,
} from "@/components/canvas-editor/shapes/shapes";
import { ToolStateNode } from "@/components/canvas-editor/utils";
import { cn } from "@/lib/utils";

function LogicDocs() {
  const editor = useEditor();
  const example = useExampleShape(new LogicShapeUtil(editor), {
    w: 300,
  });
  const example2 = useExampleShape(new LogicShapeUtil(editor), {
    type: "or",
    w: 300,
  });

  return (
    <div className="w-[400px]">
      <NodeDocsHeader>{LogicShapeUtil.docs.title}</NodeDocsHeader>
      <NodeDocsDescription>
        The Logic node allows you to combine multiple filters into a single
        filter.
      </NodeDocsDescription>
      <NodeDocsDescription>
        For the &ldquo;and&rdquo; operator, all filters must match.
      </NodeDocsDescription>
      <div className="mb-4">
        <Logic
          {...(example as unknown as React.ComponentProps<typeof Logic>)}
          className="w-full relative!"
        />
      </div>
      <NodeDocsDescription>
        For the &ldquo;or&rdquo; operator, any filter must match.
      </NodeDocsDescription>
      <Logic
        {...(example2 as unknown as React.ComponentProps<typeof Logic>)}
        className="w-full relative!"
      />
    </div>
  );
}

export class LogicTool extends ToolStateNode {
  static override id = logicType;

  static override uiToolDef = (editor: Editor) => ({
    id: logicType,
    icon: BrainCircuitIcon,
    label: "Logic",
    kbd: "e",
    defaultColor: LogicShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(logicType),
    docs: () => <LogicDocs />,
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: logicType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class LogicShapeUtil extends BindableUtil<LogicShape> {
  static override migrations = createMigrations(logicType);
  static override type = logicType;

  static override docs = {
    defaultColor: "blue" as const,
    title: "Logic",
    description: "",
  };

  override getType() {
    return logicType;
  }

  static override props: RecordProps<LogicShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    type: T.literalEnum(...logicalOps),
    padding: T.object({
      top: T.number,
      left: T.number,
      right: T.number,
      bottom: T.number,
      gap: T.number,
    }),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 140,
      minW: 140,
      h: 101,
      type: "and" as const,
      padding: {
        top: 98,
        bottom: 16,
        right: 16,
        left: 16,
        gap: 14,
      },
      metadata: undefined,
    };
  }

  override canBind({
    toShapeType,
    bindingType,
  }: TLShapeUtilCanBindOpts<LogicShape>) {
    if (bindingType === "arrow") {
      return true;
    }

    return pinnableShapes.includes(toShapeType) && bindingType === "layout";
  }

  override component(shape: LogicShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <Logic
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function Logic({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected?: boolean;
  isBound?: boolean;
  shape: LogicShape;
  className?: string;
  updateShape: (shape: LogicShape, props: Partial<LogicProps>) => void;
}) {
  const variant = shape.props.type === "and" ? "blue" : "plum";
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  const id = useValue(
    "logic-atom",
    () => {
      return dropTargetId.get();
    },
    [dropTargetId],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        isBound={isBound}
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        className={cn(
          className,
          id && shape.id === id && "rounded-lg shadow-[0_0_0_4px_#0588F0]",
        )}
        width={Math.max(shape.props.minW || 0, shape.props.w)}
        height={shape.props.h}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={shape.props.isCollapsed}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={{
            title: (
              <span className="flex items-center gap-1.5">
                <FilterIcon className="h-3.5 w-3.5" />
                Logic
              </span>
            ),
            description: (
              <div className="flex flex-col gap-2">
                <p>Combines several rules into one decision about a post.</p>
                <p>
                  <strong>All of these</strong>: a post must pass every rule inside
                  this block. <strong>Any of these</strong>: passing one is enough.
                </p>
                <p>
                  Drag rules from the Rules palette (the filter icon at the left)
                  into this block. Put another Logic block inside for mixes, like
                  &ldquo;all of these, and any of those&rdquo;.
                </p>
              </div>
            ),
            variant: variant,
          }}
        />
        <BlockBody>
          <div className="p-1 flex-1 flex">
            <CanvasSimpleSelect
              aria-label="Logical Operator"
              value={shape.props.type}
              placeholder="Type"
              className="flex-1"
              options={logicalOpOptions}
              onValueChange={(v) =>
                updateShape(shape, { type: v as LogicalOp })
              }
            />
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
